/**
 * Get the platform domain from environment variables
 * Returns the platform domain without protocol or www prefix
 * Falls back to deriving from current hostname if env var is not set
 */
export const getPlatformDomain = (): string => {
  if (typeof window === "undefined") return "localhost";

  const platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN;
  if (platformDomain) {
    // Remove protocol if present
    let domain = platformDomain.replace(/^https?:\/\//, "");
    // Remove www prefix
    domain = domain.replace(/^www\./, "");
    // Remove port if present
    domain = domain.split(":")[0];

    return domain.toLowerCase();
  }

  // If env var is not set, try to derive from current hostname
  // This is useful for staging/production where env var might not be set
  const hostname = window.location.hostname.toLowerCase();
  const host = hostname.split(":")[0];

  // For localhost, default to localhost
  if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
    return "localhost";
  }

  // For localhost subdomains, return localhost as platform domain
  if (host.includes("localhost") || host.includes(".local")) {
    return "localhost";
  }

  // For production/staging domains, try to extract the platform domain
  // If hostname is "staging.ushoplify.com", that's the platform domain
  // If hostname is "store1.staging.ushoplify.com", extract "staging.ushoplify.com"
  // If hostname is "store1.ushoplify.com", extract "ushoplify.com"
  const hostParts = host.split(".");

  // If it's a 2-part domain (e.g., "ushoplify.com"), that's the platform domain
  if (hostParts.length === 2) {
    return host;
  }

  // If it's a 3-part domain, check if middle part is "staging" or "www"
  // e.g., "staging.ushoplify.com" -> "staging.ushoplify.com"
  // e.g., "www.ushoplify.com" -> "ushoplify.com"
  if (hostParts.length === 3) {
    if (hostParts[0] === "staging" || hostParts[0] === "www") {
      // For staging, return the full domain; for www, return without www
      return hostParts[0] === "staging" ? host : `${hostParts[1]}.${hostParts[2]}`;
    }
    // Otherwise it's likely a store subdomain, extract base domain
    return `${hostParts[1]}.${hostParts[2]}`;
  }

  // For 4+ part domains (e.g., "store1.staging.ushoplify.com"), extract base
  // Take the last 2 parts for base domain, or last 3 if second-to-last is "staging"
  if (hostParts.length >= 4) {
    const secondToLast = hostParts[hostParts.length - 3];
    if (secondToLast === "staging") {
      // e.g., "store1.staging.ushoplify.com" -> "staging.ushoplify.com"
      return `${hostParts.slice(hostParts.length - 3).join(".")}`;
    }
    // Otherwise, take last 2 parts
    return `${hostParts.slice(hostParts.length - 2).join(".")}`;
  }

  // Fallback to localhost for development
  return "localhost";
};

/**
 * Check if the current hostname is a platform host (main domain)
 * Returns true if the hostname matches the platform domain, false otherwise.
 * This ensures custom domains are treated as store sites, not platform sites.
 */
export const isPlatformHost = (): boolean => {
  if (typeof window === "undefined") return false;

  const hostname = window.location.hostname.toLowerCase();
  const platformDomain = getPlatformDomain();

  // Remove port if present
  const host = hostname.split(":")[0];

  // Handle localhost and .local main hostnames
  if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") {
    // If platform domain is also localhost, this is the platform host
    return platformDomain === "localhost" || platformDomain === "127.0.0.1";
  }

  // For localhost subdomains (store1.localhost), check if it matches platform domain
  if (host.includes("localhost") || host.includes(".local")) {
    // If it's exactly "localhost" or matches platform domain exactly
    if (host === "localhost" || host === platformDomain) {
      return true;
    }
    // If platform domain is localhost and current host is a subdomain, it's not platform
    if (platformDomain === "localhost" && host.endsWith(".localhost")) {
      return false;
    }
    // For .local domains, check exact match
    if (host === platformDomain) {
      return true;
    }
    // Otherwise it's a store subdomain
    return false;
  }

  // For production/staging, check if host matches platform domain exactly
  // Exact match: host === platformDomain
  const isExactMatch = host === platformDomain;

  // www prefix match: host === www.platformDomain
  const isWwwMatch = host === `www.${platformDomain}`;

  // Staging subdomain match: host === staging.platformDomain
  // Only matches staging.platformDomain, not staging.store1.com or other custom domains
  const isStagingMatch = host === `staging.${platformDomain}`;

  // It's a platform host if it's an exact match, www match, or staging.platformDomain
  // All other hosts (including store subdomains, custom domains, and staging.store1.com) are store hosts
  return isExactMatch || isWwwMatch || isStagingMatch;
};

/**
 * Determines if the current hostname is a store host (not the platform domain).
 * Returns true for store subdomains and custom domains.
 * Returns false only for the actual platform domain.
 */
export const isStoreHost = (): boolean => {
  return !isPlatformHost();
};

